import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MdAccountBalance, 
  MdReceipt, 
  MdBook, 
  MdBalance, 
  MdCalendarToday, 
  MdInsertChart, 
  MdBusiness,
  MdAttachMoney,
  MdAccountBalanceWallet,
  MdArrowBack
} from 'react-icons/md';
import GradientCard from '../Common/GradientCard';
import accountingService from '../../services/accountingService';

const AccountingLanding: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ accounts: 0, periods: 0, journalDraft: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const accountsRes = await accountingService.getAccounts();
        setSummary({
          accounts: accountsRes.data?.length || 0,
          periods: 0,
          journalDraft: 0,
        });
      } catch (error) {
        console.error('Failed to load summary:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSummary();
  }, []);

  const sections = [
    {
      title: 'Chart of Accounts',
      description: 'Manage your complete list of accounts — assets, liabilities, equity, revenue and expenses',
      icon: <MdAccountBalance />,
      path: '/accounting/chart-of-accounts',
      gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      badge: summary.accounts > 0 ? `${summary.accounts} accounts` : undefined,
    },
    {
      title: 'Journal Entries',
      description: 'Record and review double-entry transactions. Every debit must have a matching credit.',
      icon: <MdReceipt />,
      path: '/accounting/journal-entry',
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
      badge: summary.journalDraft > 0 ? `${summary.journalDraft} drafts` : undefined,
    },
    {
      title: 'General Ledger',
      description: 'Full transaction history for any account with running balance and period filtering',
      icon: <MdBook />,
      path: '/accounting/ledger',
      gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    },
    {
      title: 'Trial Balance',
      description: 'Verify that total debits equal total credits across all accounts',
      icon: <MdBalance />,
      path: '/accounting/reports/trial-balance',
      gradient: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
    },
    {
      title: 'Accounting Periods',
      description: 'Manage monthly accounting periods — open, close and lock periods to control posting',
      icon: <MdCalendarToday />,
      path: '/accounting/periods',
      gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
    },
    {
      title: 'Financial Reports',
      description: 'Profit & Loss and Balance Sheet reports generated from your double-entry ledger',
      icon: <MdInsertChart />,
      path: '/accounting/reports',
      gradient: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
    },
    {
      title: 'Fixed Assets',
      description: 'Manage fixed assets and run monthly depreciation with automatic journal posting',
      icon: <MdBusiness />,
      path: '/accounting/depreciation',
      gradient: 'linear-gradient(135deg, #78716c 0%, #a8a29e 100%)',
    },
    {
      title: 'Budgets & Forecasts',
      description: 'Set budgets and compare against actual journal entry totals',
      icon: <MdAttachMoney />,
      path: '/accounting/budgets',
      gradient: 'linear-gradient(135deg, #ea580c 0%, #fb923c 100%)',
    },
    {
      title: 'Bank Reconciliation',
      description: 'Match bank statements against journal entries and close reconciled periods',
      icon: <MdAccountBalanceWallet />,
      path: '/accounting/reconciliation',
      gradient: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
    },
  ];

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={() => navigate('/dashboard')}>
            <MdArrowBack />
            <span>Back to Dashboard</span>
          </BackButton>
          <div>
            <Title>Accounting</Title>
            <Subtitle>
              Complete double-entry accounting system with chart of accounts, journal entries, and financial reports
            </Subtitle>
          </div>
        </HeaderContent>
      </Header>

      <InfoBanner>
        <BannerIcon><MdBalance /></BannerIcon>
        <BannerContent>
          <BannerTitle>Double-Entry Accounting</BannerTitle>
          <BannerText>
            Every transaction touches at least two accounts. Debits always equal credits. 
            Assets = Liabilities + Equity at all times.
          </BannerText>
        </BannerContent>
      </InfoBanner>

      {loading ? (
        <LoadingState>Loading accounting modules...</LoadingState>
      ) : (
        <SectionsGrid>
          {sections.map((section) => (
            <GradientCard key={section.path} {...section} />
          ))}
        </SectionsGrid>
      )}
    </Container>
  );
};


const Container = styled.div`
  min-height: calc(100vh - 60px);
  background: #F8F9FA;
  padding: 32px;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 28px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: fit-content;

  svg {
    font-size: 18px;
  }

  &:hover {
    border-color: #1e3a8a;
    color: #1e3a8a;
    background: #eff6ff;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #6B7280;
  margin: 6px 0 0 0;
  line-height: 1.6;
  max-width: 800px;
`;

const InfoBanner = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  margin-bottom: 32px;
  border-radius: 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
`;

const BannerIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #1e3a8a;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const BannerContent = styled.div`
  flex: 1;
`;

const BannerTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e3a8a;
  margin-bottom: 6px;
`;

const BannerText = styled.div`
  font-size: 14px;
  color: #1e40af;
  line-height: 1.6;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #9CA3AF;
  font-size: 15px;
`;

export default AccountingLanding;

